import type Dialog from '@naturehouse/design-system/components/molecules/dialog/Dialog';
import type { LoginResponseType } from '@naturehouse/design-system/components/organisms/login-form/LoginForm';
import type LoginForm from '@naturehouse/design-system/components/organisms/login-form/LoginForm';
import { LoginEvents } from '@naturehouse/design-system/components/organisms/login-form/LoginForm';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import type { RefObject } from 'react';
import { createRef } from 'react';
import { captureException } from '@sentry/browser';
import GTM from '../../gtm/GTM';
import TrackLogin from './TrackLogin';
import LoginManager from './LoginManager';
import CSRFTokenCollection from './CSRFTokenCollection';
import UserStateManager from './UserStateManager';

export interface LoginDialogInterface {
    show(): void;

    close(): void;

    successCallback: (response: LoginResponseType) => Promise<void>;
}

export default class LoginDialog implements LoginDialogInterface {
    static #instance: LoginDialog | null = null;

    static getInstance(dialogId: string = 'login-dialog'): LoginDialog {
        if (this.#instance === null || this.#instance.dialogId !== dialogId) {
            this.#instance = new LoginDialog(dialogId);
        }

        return this.#instance;
    }

    static destroyInstance(dialogId?: string): void {
        if (this.#instance === null || this.#instance.dialogId !== dialogId) {
            return;
        }

        this.#instance = null;
    }

    static destroy(): void {
        this.#instance = null;
    }

    readonly #dialog: Dialog;

    readonly #form: LoginForm;

    readonly #captchaRef: RefObject<TurnstileInstance | null> = createRef<TurnstileInstance>();

    readonly #userStateManager: UserStateManager = UserStateManager.getInstance();

    public constructor(private readonly dialogId: string) {
        this.#dialog = document.getElementById(this.dialogId) as Dialog;
        this.#form = this.#dialog?.querySelector('[is="nh-login-form"]') as LoginForm;

        if (this.#dialog === null || this.#form === null) {
            return;
        }

        this.#form.addEventListener(LoginEvents.LOGIN_SUCCESS, this.#handleSuccess);
        this.#form.addEventListener(LoginEvents.LOGIN_FAILED, this.#handleFailed);
    }

    public async show(): Promise<void> {
        this.#form.disableSubmitButtons();
        this.#dialog.showModal();
        const data = await CSRFTokenCollection.getInstance().retrieve({ name: 'authenticate' });

        if (data) {
            this.#form.token = data.token;
        }

        await this.#renderCaptchaComponent();

        this.#form.enableSubmitButtons();
    }

    public close(): void {
        this.#dialog.close();
        LoginDialog.destroy();
    }

    public async successCallback(response: LoginResponseType): Promise<void> {
        const redirectURL = response.isLandlord
            ? this.#dialog.dataset.landlordRedirect
            : this.#dialog.dataset.userRedirect;

        if (!redirectURL) {
            try {
                await GTM.setMetaUserHash(response.personalInformation.email);
                GTM.setMetaUserId(response.uuid);
                GTM.setMetaLoginMethod('email');
                TrackLogin.trackLoginEvent();
            } catch {
                // continue regardless of errors
            }

            return;
        }

        window.location.href = redirectURL;
    }

    readonly #handleSuccess = async (e: Event): Promise<void> => {
        const event = e as CustomEvent;
        this.#userStateManager.updateState({
            isLoggedIn: true,
            isLandlord: event.detail.isLandlord ?? false
        });
        await this.successCallback(event.detail);

        const loginManager = LoginManager.getInstance();
        loginManager.isLoggedIn = true;

        this.#dialog.close();
    };

    readonly #handleFailed = (): void => {
        this.#form.disableSubmitButtons();
        this.#captchaRef.current?.reset();
        this.#userStateManager.updateState({
            isLoggedIn: false,
            isLandlord: false
        });
        this.#form.enableSubmitButtons();
    };

    readonly #renderCaptchaComponent = (): Promise<void> => {
        const input = this.#form.querySelector<HTMLInputElement>('[data-role="captcha-input"]');
        const clone = input?.cloneNode(true) as HTMLInputElement | null;
        const parent = input?.parentElement;

        return new Promise<void>((resolve, reject): void => {
            document.dispatchEvent(
                new CustomEvent('render-captcha', {
                    detail: {
                        input,
                        options: {
                            onSuccess: (): void => {
                                resolve();

                                if (!clone || !parent) {
                                    captureException('Either clone or parent is null');
                                    return;
                                }

                                parent.appendChild(clone);
                            },
                            onError: (error: any): void => reject(error)
                        },
                        ref: this.#captchaRef
                    }
                })
            );
        });
    };
}
